import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'

import type { ImportResult } from '@/lib/api/customer-api'

import { Badge } from '@/components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { m } from '@/lib/paraglide/messages'

interface ImportResultDialogProps {
    result: ImportResult | null
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function ImportResultDialog({
    result,
    open,
    onOpenChange,
}: ImportResultDialogProps) {
    if (!result) return null

    // Kết quả import có thể tới từ SSE của bản server cũ (chưa có hai trường
    // này), nên đọc phòng thủ thay vì tin tuyệt đối vào kiểu dữ liệu.
    const warnings = result.warnings ?? []
    const debtAdjusted = result.debt_adjusted ?? 0
    const hasErrors = result.errors.length > 0
    const hasWarnings = warnings.length > 0
    const total = result.imported + result.updated + result.skipped

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {hasErrors || hasWarnings ? (
                            <AlertTriangle className="size-5 text-amber-500" />
                        ) : (
                            <CheckCircle2 className="size-5 text-green-500" />
                        )}
                        {m.import_result_title()}
                    </DialogTitle>
                </DialogHeader>

                {/* Summary badges */}
                <div className="flex flex-wrap gap-2">
                    {result.imported > 0 && (
                        <Badge variant="success">
                            {m.import_new_count({ count: result.imported })}
                        </Badge>
                    )}
                    {result.updated > 0 && (
                        <Badge variant="secondary">
                            {m.import_updated_count({ count: result.updated })}
                        </Badge>
                    )}
                    {debtAdjusted > 0 && (
                        <Badge variant="secondary">
                            {m.import_adjusted_count({ count: debtAdjusted })}
                        </Badge>
                    )}
                    {result.skipped > 0 && (
                        <Badge variant="outline">
                            {m.import_skipped_count({ count: result.skipped })}
                        </Badge>
                    )}
                    {hasWarnings && (
                        <Badge variant="outline">
                            {m.import_warning_count({ count: warnings.length })}
                        </Badge>
                    )}
                    {hasErrors && (
                        <Badge variant="destructive">
                            {m.import_error_count({
                                count: result.errors.length,
                            })}
                        </Badge>
                    )}
                </div>

                {/* Progress bar */}
                <div className="flex h-2 overflow-hidden rounded-full bg-muted">
                    {result.imported > 0 && (
                        <div
                            className="bg-green-500"
                            style={{
                                width: `${(result.imported / (total + result.errors.length)) * 100}%`,
                            }}
                        />
                    )}
                    {result.updated > 0 && (
                        <div
                            className="bg-blue-500"
                            style={{
                                width: `${(result.updated / (total + result.errors.length)) * 100}%`,
                            }}
                        />
                    )}
                    {result.skipped > 0 && (
                        <div
                            className="bg-gray-400"
                            style={{
                                width: `${(result.skipped / (total + result.errors.length)) * 100}%`,
                            }}
                        />
                    )}
                    {hasErrors && (
                        <div
                            className="bg-red-500"
                            style={{
                                width: `${(result.errors.length / (total + result.errors.length)) * 100}%`,
                            }}
                        />
                    )}
                </div>

                {/* Warning details — rows the import handled by making a
                    judgement call the operator should double-check. */}
                {hasWarnings && (
                    <div className="max-h-40 overflow-auto rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-16">
                                        {m.import_row()}
                                    </TableHead>
                                    <TableHead className="w-28">
                                        {m.customer_phone()}
                                    </TableHead>
                                    <TableHead>
                                        {m.import_warning_detail()}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {warnings.map((w, i) => (
                                    <TableRow key={i}>
                                        <TableCell className="font-mono text-xs">
                                            {w.row}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {w.phone || '—'}
                                        </TableCell>
                                        <TableCell className="text-xs text-amber-600 dark:text-amber-500">
                                            <span className="flex items-start gap-1">
                                                <AlertTriangle className="mt-px size-3 shrink-0" />
                                                {w.message}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {/* Error details table */}
                {hasErrors && (
                    <div className="max-h-60 overflow-auto rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-16">
                                        {m.import_row()}
                                    </TableHead>
                                    <TableHead className="w-24">
                                        {m.customer_code()}
                                    </TableHead>
                                    <TableHead>
                                        {m.import_error_detail()}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {result.errors.map((err, i) => (
                                    <TableRow key={i}>
                                        <TableCell className="font-mono text-xs">
                                            {err.row}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {err.code || '—'}
                                        </TableCell>
                                        <TableCell className="text-xs text-destructive">
                                            <span className="flex items-center gap-1">
                                                <XCircle className="size-3 shrink-0" />
                                                {err.error}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
